<template>
  <AppSidebar>
    <div class="space-y-10">
      <!-- Page Header -->
      <div class="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-8 border-b border-gray-100 dark:border-gray-800">
        <div>
          <h1 class="text-3xl font-bold text-gray-900 dark:text-white leading-none mb-2" style="font-family: Poppins, sans-serif">Platform Settings</h1>
          <p class="text-sm text-gray-500 dark:text-gray-400 font-medium">Manage site configuration & system preferences</p>
        </div>
      </div>
      
      <div class="grid grid-cols-1 lg:grid-cols-4 gap-10">
        <!-- Settings Navigation -->
        <div class="lg:col-span-1">
          <nav class="space-y-1.5 sticky top-8">
            <button
              v-for="tab in settingsTabs"
              :key="tab.id"
              @click="activeTab = tab.id"
              class="w-full text-left px-5 py-4 rounded-2xl font-bold uppercase tracking-widest text-xs transition-all duration-300 group relative overflow-hidden"
              :class="activeTab === tab.id 
                ? 'bg-primary-600 text-white shadow-lg shadow-primary-500/20' 
                : 'text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-white'"
            >
              <div class="flex items-center space-x-4 relative z-10">
                <div v-html="tab.icon" class="w-4 h-4" :class="activeTab === tab.id ? 'text-white' : 'text-gray-400 dark:text-gray-600 group-hover:text-primary-500'"></div>
                <span>{{ tab.name }}</span>
              </div>
            </button>
          </nav>
        </div>
        
        <!-- Settings Content -->
        <div class="lg:col-span-3 space-y-8">
          <!-- General Settings -->
          <div v-if="activeTab === 'general'" class="bg-white dark:bg-gray-800/50 rounded-[32px] border border-gray-100 dark:border-gray-700/50 shadow-sm overflow-hidden">
            <div class="p-8 sm:p-10 border-b border-gray-50 dark:border-gray-700/30">
              <h2 class="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-widest mb-2" style="font-family: Poppins, sans-serif">General Configuration</h2>
              <p class="text-xs text-gray-500 dark:text-gray-400 font-medium">Fundamental identity & core platform settings</p>
            </div>
            <div class="p-8 sm:p-10 space-y-8">
              <div v-for="setting in groupedSettings.general" :key="setting.id">
                <label class="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2.5">{{ setting.label }}</label>
                <input
                  v-if="['text', 'email', 'phone', 'url', 'number'].includes(setting.type)"
                  v-model="setting.value"
                  :type="setting.type === 'phone' ? 'text' : setting.type"
                  class="w-full px-5 py-3.5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium"
                  :placeholder="setting.description"
                />
                <textarea
                  v-else-if="setting.type === 'textarea'"
                  v-model="setting.value"
                  rows="4"
                  class="w-full px-5 py-3.5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium resize-none"
                  :placeholder="setting.description"
                ></textarea>
              </div>
            </div>
          </div>
          
          <!-- Other Settings Groups -->
          <div v-if="['contact', 'social', 'seo', 'hero', 'about', 'services', 'contact_page'].includes(activeTab)" class="bg-white dark:bg-gray-800/50 rounded-[32px] border border-gray-100 dark:border-gray-700/50 shadow-sm overflow-hidden">
            <div class="p-8 sm:p-10 border-b border-gray-50 dark:border-gray-700/30">
              <h2 class="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-widest mb-2" style="font-family: Poppins, sans-serif">{{ formatGroupName(activeTab) }} Settings</h2>
              <p class="text-xs text-gray-500 dark:text-gray-400 font-medium">Configure {{ formatGroupName(activeTab).toLowerCase() }} preferences</p>
            </div>
            <div class="p-8 sm:p-10 space-y-8">
              <div v-for="setting in groupedSettings[activeTab]" :key="setting.id" class="group/field">
                <label class="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2.5 group-hover/field:text-primary-500 transition-colors">{{ setting.label }}</label>
                <input
                  v-if="['text', 'email', 'phone', 'url', 'number'].includes(setting.type)"
                  v-model="setting.value"
                  :type="setting.type === 'phone' ? 'text' : setting.type"
                  class="w-full px-5 py-3.5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium"
                  :placeholder="setting.description"
                />
                <textarea
                  v-else-if="setting.type === 'textarea'"
                  v-model="setting.value"
                  rows="4"
                  class="w-full px-5 py-3.5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium resize-none"
                  :placeholder="setting.description"
                ></textarea>
              </div>
            </div>
          </div>

          <!-- System Settings -->
          <div v-if="activeTab === 'system'" class="bg-white dark:bg-gray-800/50 rounded-[32px] border border-gray-100 dark:border-gray-700/50 shadow-sm overflow-hidden">
            <div class="p-8 sm:p-10 border-b border-gray-50 dark:border-gray-700/30">
              <h2 class="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-widest mb-2" style="font-family: Poppins, sans-serif">System Switches</h2>
              <p class="text-xs text-gray-500 dark:text-gray-400 font-medium">Active platform flags & feature toggles</p>
            </div>
            <div class="p-8 sm:p-10 space-y-4">
              <div v-for="setting in groupedSettings.system" :key="setting.id" class="flex items-center justify-between p-6 bg-gray-50/50 dark:bg-gray-900/30 rounded-2xl border border-transparent hover:border-primary-500/20 transition-all group">
                <div>
                  <h3 class="text-[11px] font-bold text-gray-900 dark:text-white uppercase tracking-widest mb-1">{{ setting.label }}</h3>
                  <p class="text-xs text-gray-500 dark:text-gray-400 font-medium italic">{{ setting.description }}</p>
                </div>
                <label class="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    v-model="setting.value" 
                    true-value="1" 
                    false-value="0" 
                    class="sr-only peer"
                  >
                  <div class="w-14 h-7 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-gray-300 dark:after:border-gray-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>
            </div>
          </div>
          
          <!-- Save Button -->
          <div class="flex justify-end pt-10 border-t border-gray-100 dark:border-gray-800">
            <button 
              @click="saveSettings" 
              class="w-full sm:w-auto px-10 py-4 bg-primary-600 hover:bg-primary-700 text-white text-[10px] font-black uppercase tracking-widest rounded-2xl shadow-xl shadow-primary-500/20 transition-all active:scale-95"
            >
              Save Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  </AppSidebar>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useUIStore } from '@/stores/ui'
import { useSettingsStore } from '@/stores/settings'
import AppSidebar from '@/components/layout/AppSidebar.vue'

const uiStore = useUIStore()
const settingsStore = useSettingsStore()

// State
const activeTab = ref('general')

// Settings tabs
const settingsTabs = [
  { id: 'general', name: 'General', icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>' },
  { id: 'contact', name: 'Contact Info', icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>' },
  { id: 'social', name: 'Social Media', icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path></svg>' },
  { id: 'seo', name: 'SEO', icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>' },
  { id: 'system', name: 'System', icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"></path></svg>' },
  { id: 'hero', name: 'Hero Sec.', icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"></path></svg>' }
]

// Grouped settings computed property
const groupedSettings = computed(() => {
  const groups = {}
  settingsStore.allSettings.forEach(setting => {
    if (!groups[setting.group]) {
      groups[setting.group] = []
    }
    groups[setting.group].push({ ...setting }) // Clone to avoid direct store mutation before save
  })
  return groups
})

// Initialize
onMounted(async () => {
  await settingsStore.fetchAllSettings()
})

// Methods
const formatGroupName = (name) => {
  return name.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
}

const saveSettings = async () => {
  try {
    const settingsToUpdate = []
    // Collect all settings from all groups
    Object.values(groupedSettings.value).forEach(group => {
      group.forEach(setting => {
        settingsToUpdate.push({
          id: setting.id,
          value: setting.value
        })
      })
    })

    const success = await settingsStore.bulkUpdateSettings(settingsToUpdate)
    
    if (success) {
      uiStore.showNotification({
        type: 'success',
        title: 'Settings Saved',
        message: 'Your settings have been saved successfully.'
      })
    }
  } catch (error) {
    uiStore.showNotification({
      type: 'error',
      title: 'Save Failed',
      message: 'Failed to save settings.'
    })
  }
}
</script>


